import type { FC } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useSession } from '@packages/shared/src/components/SessionProvider/useSession';

import type { GTMEventPageMetaData } from '../../hooks/useTracking/types';
import { useIsBot, useTracking } from '../../hooks/useTracking';
import { useCustomerInformation } from './useCustomerInformation';

export type PageMetaDataProps = Omit<
  GTMEventPageMetaData,
  'event' | 'eventAction' | 'eventCategory' | 'eventLabel' | 'eventValue' | 'page'
> & {
  /** page meta data information */
  page: {
    /** the page type (e.g. 'serp', 'detailview') */
    type: string;
    /** optional template information */
    template?: string;
  };
};

/**
 * This component dispatches the following GTM events:
 * - dataLayerReady
 * - pageMetaData
 * */
export const PageMetaData: FC<PageMetaDataProps> = ({
  page,
  categoryPath,
  marketcode,
  query,
  searchId,
}) => {
  const dispatchGTMEvent = useTracking();
  const isBot = useIsBot();
  const { isLoading, customerInformation } = useCustomerInformation();
  const { loggedIn } = useSession();
  const intl = useIntl();

  const { type, template } = page;

  useEffect(() => {
    if (!isLoading) {
      dispatchGTMEvent({
        event: 'dataLayerReady',
        Sprache: intl.locale,
        ...customerInformation,
        visitor: {
          ...customerInformation?.visitor,
          login: loggedIn,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, customerInformation?.visitor.login, intl.locale, loggedIn]);

  useEffect(() => {
    const event: GTMEventPageMetaData = {
      event: 'pageMetaData',
      page: {
        type,
        template,
        bot: isBot,
      },
      categoryPath,
      marketcode,
      query,
      searchId,
    };
    if (typeof window !== 'undefined') {
      (window as any).pagetype = type;
    }
    dispatchGTMEvent(event);
    // remove categoryPath from array, it is not a primitive value and is kinda unpredictable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, template, marketcode, query, searchId, isBot]);

  return <div data-testid="page-meta-data" />;
};
